import { AnimationBuilder } from '@angular/animations';
import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { UserDataService, UserSessionService } from '@com-delta-idp/mnx-oauth';
import {
  AnalyticsService,
  HEADER_TOP_MENU,
  LoggingService
} from '@com-delta-idp/mnx-utils';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  skip,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import { DataLayer } from '../../common/datalayer/datalayer';
import { DeviceTypeHelper } from '../../common/device-type-helper';
import { ApplicationConfig } from '../../config/config';
import { ContentMyDelta } from '../../model/content/contentmydelta.model';
import {
  ContentPopularTopic,
  ContentSearchFeed
} from '../../model/content/contentsearchfeed.model';
import { SearchResult } from '../../model/search-result.model';
import {
  SiteSearchPERequest,
  SiteSearchPEResponse
} from '../../model/site-search-pe.model';
import { AdobeDataLayerService } from '../../services/adobe-data-layer/adobe-data-layer';
import { CommonService } from '../../services/common.service';
import { ContentHeaderFooterService } from '../../services/content/contentheaderfooter.service';
import { SearchService } from '../../services/search/search.service';
import { SiteSearchPeService } from '../../services/search/site-search-pe.service';

const autocomplete = (time, selector) => (source$) =>
  source$.pipe(
    debounceTime(time),
    distinctUntilChanged(),
    switchMap((...args: any[]) =>
      selector(...args).pipe(takeUntil(source$.pipe(skip(1))))
    )
  );

@Component({
  selector: 'ngc-flyout-search',
  templateUrl: 'flyout-search.component.html'
})
export class FlyoutSearchComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('dialog') dialog: any;
  @ViewChild('searchField') searchField: any;

  contentHeaderFooterServiceSubscribe: Subscription;
  contentPassgengers: any = [];
  contentPopularTopic: ContentPopularTopic[];
  contentSearch: ContentSearchFeed;
  deviceTypes: any = [];
  deviceTypeSubscribe: Subscription;
  firstFocusableEl: any = [];
  flyoutContent: ContentMyDelta;
  flyOutLocation: any;
  flyOutPositionHeight: any;
  flyoutSearchObj: any;
  focusableEls: any = [];
  footerSearchModalLoaded = false;
  headerSearchModalLoaded = false;
  isDelta: boolean;
  isIphone = !!navigator.platform.match(/iPhone/);
  isSearchFocused: boolean;
  lastFocusableEl: any = [];
  searchResults: SearchResult[] = [];
  showEmptyMessage = false;
  showSearch = true;
  siteSearchUrl = '/site-search?searchTerm=';

  private unsubscribe$: Subject<void> = new Subject();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private _builder: AnimationBuilder,
    private _deviceTypeHelper: DeviceTypeHelper,
    private appconfig: ApplicationConfig,
    private cdref: ChangeDetectorRef,
    private contentService: ContentHeaderFooterService,
    private datalayer: DataLayer,
    private el: ElementRef,
    private searchService: SearchService,
    private srv: CommonService,
    public bsModalRef: BsModalRef,
    private loggingService: LoggingService,
    private analytics: AnalyticsService,
    private userSession: UserSessionService,
    private adobeDataLayer: AdobeDataLayerService,
    public siteSearchService: SiteSearchPeService,
    public userDataService: UserDataService
  ) {
    this.contentSearch = new ContentSearchFeed();
    this.contentPopularTopic = this.contentSearch.popularTopics;
  }

  @HostListener('window:resize', ['$event']) onResize(event) {
    this.flyoutSearchObj = this.srv.flyoutPosition('search', false);
    this.flyOutLocation =
      this.flyoutSearchObj !== undefined && this.flyoutSearchObj !== null
        ? this.flyoutSearchObj.flyOutLocation
        : '';
    this.flyOutPositionHeight =
      this.flyoutSearchObj !== undefined && this.flyoutSearchObj !== null
        ? this.flyoutSearchObj.flyOutPositionHeight
        : 0;
    if (isPlatformBrowser(this.platformId)) {
      const modelContainer = document.getElementsByTagName('modal-container');
      if (document.body.scrollWidth < 992) {
        if (
          modelContainer !== undefined &&
          modelContainer !== null &&
          modelContainer.length > 0 &&
          modelContainer[0].classList.contains('search-header-modal')
        ) {
          document
            .getElementsByClassName('search-flyout')[0]
            .classList.remove('search-flyout-hidden');
        }
      } else {
        if (
          modelContainer !== undefined &&
          modelContainer !== null &&
          modelContainer.length > 0
        ) {
          modelContainer[0].classList.add('search-header-modal');
        }
      }
    }
  }

  ngOnInit() {
    this.getSiteSearchUrl();
    this.flyoutSearchObj = this.srv.flyoutPosition('search', false);
    this.flyOutLocation =
      this.flyoutSearchObj !== undefined && this.flyoutSearchObj !== null
        ? this.flyoutSearchObj.flyOutLocation
        : '';
    this.flyOutPositionHeight =
      this.flyoutSearchObj !== undefined && this.flyoutSearchObj !== null
        ? this.flyoutSearchObj.flyOutPositionHeight
        : 0;
    const searchModalContainer = document.querySelector('.search-header-modal');
    if (searchModalContainer) {
      document
        .querySelector('.search-header-modal')
        .setAttribute('aria-label', 'search');
    }
    if (this.appconfig.onBrowser) {
      this.deviceTypeSubscribe = this._deviceTypeHelper
        .deviceDetection()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((resp) => {
          this.deviceTypes = resp;
        });
    }
    if (this.contentService.contentHeaderFooter) {
      this.contentHeaderFooterServiceSubscribe =
        this.contentService.contentHeaderFooter
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(
            (data) => {
              if (data) {
                this.contentSearch =
                  data.header.auxiliaryNavigation.get('searchfeed').details;
                this.flyoutContent = data.header.auxiliaryNavigation.get(
                  'myFlightPanel'
                )
                  ? data.header.auxiliaryNavigation.get('myFlightPanel').details
                  : null;
                if (
                  this.flyoutContent.isDelta !== undefined &&
                  this.flyoutContent.isDelta !== ''
                ) {
                  this.isDelta =
                    this.flyoutContent.isDelta.toUpperCase() === 'TRUE'
                      ? true
                      : false;
                } else {
                  this.isDelta = true;
                }
                this.contentPopularTopic = this.contentSearch.popularTopics;
                this.cdref.detectChanges();
              }
            },
            (err) => {
              this.loggingService.log('Caught Error: ' + err);
            }
          );
    }
  }

  updateAnalytics(url: string) {
    this.adobeDataLayer.updateAnalytics(url, HEADER_TOP_MENU.GLOBAL_SEARCH);
    window.location.href = url;
  }

  ngAfterViewInit() {
    const modalElement = document.getElementsByTagName('modal-container');
    if (modalElement && modalElement.length > 0) {
      modalElement[0].addEventListener(
        'click',
        this.backDropClickHandler.bind(this)
      );
      modalElement[0].setAttribute('aria-label', 'Search');
      if (this.isIphone) {
        if (this.deviceTypes.isMobile) {
          window.scrollTo(0, 0);
        }
      }
    }
    this.trapFocusInsideModal();
  }

  backDropClickHandler(event) {
    const flyoutSearch = event.target.parentElement;
    if (flyoutSearch) {
      const isModalContainerClicked =
        event.target.classList.contains('search-header-modal') ||
        event.target.classList.contains('search-footer-modal') ||
        flyoutSearch.classList.contains('modal-content');
      if (isModalContainerClicked) {
        this.bsModalRef.hide();
        this.headerSearchModalLoaded = false;
        this.showSearch = false;
        document.getElementsByTagName('html')[0].classList.remove('no-scroll');
      }
    }
  }

  close() {
    this.showSearch = false;
    this.bsModalRef.hide();
    document.getElementsByTagName('html')[0].classList.remove('no-scroll');
  }

  onSearchFieldFocus() {
    this.isSearchFocused = true;
  }

  stripSpecialCharacters(input: string): string {
    const regex = /[!*()]/g;
    return input.replace(regex, '');
  }

  onSearchSubmit(): void {
    window.location.href =
      (this.siteSearchUrl ? this.siteSearchUrl : '/site-search?searchTerm=') +
      encodeURIComponent(
        this.stripSpecialCharacters(
          this.searchField?.nativeElement?.value || ''
        )
      );
  }

  onSearchFieldFocusout() {
    this.isSearchFocused = false;
  }

  handleResults(results: SearchResult[]) {
    this.showEmptyMessage = false;
    this.searchResults = results;

    if (results.length === 0) {
      this.showEmptyMessage = true;
    } else {
      this.showEmptyMessage = false;
      const location = this.headerSearchModalLoaded ? 'header' : 'footer';
      this.saveDataForAnalytics(location, this.searchField.nativeElement.value);
    }
  }

  saveDataForAnalytics(location: 'header' | 'footer', term: string) {
    document.dispatchEvent(
      new CustomEvent(`site-search-${location}-event`, { bubbles: true })
    );
    this.datalayer.setSiteSearch(term, true);
  }

  modalTabEvent(KEYCODE_TAB, activeModalElement) {
    activeModalElement.addEventListener('keydown', (e: any) => {
      if (e.key === 'Tab' || e.keyCode === KEYCODE_TAB) {
        this.firstFocusableEl =
          getComputedStyle(this.focusableEls[0].parentElement).display ===
          'none'
            ? this.focusableEls[1]
            : this.focusableEls[0];
        this.lastFocusableEl =
          this.el.nativeElement.querySelector('.display-topicLink')
            .clientWidth === 0
            ? this.el.nativeElement
                .querySelector('.display-topicLink')
                .previousElementSibling.querySelector('a')
            : this.focusableEls[this.focusableEls.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === this.firstFocusableEl) {
            this.lastFocusableEl.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === this.lastFocusableEl) {
            this.firstFocusableEl.focus();
            e.preventDefault();
          }
        }
      }
    });
  }

  trapFocusInsideModal() {
    const activeModalElement = this.el.nativeElement.children[0].children[0];
    if (activeModalElement) {
      const tabbableEls = activeModalElement.querySelectorAll(`a[href],
    area[href],
    input:not([disabled]),
    select:not([disabled]),
    textarea:not([disabled]),
    button:not([disabled]),
    [tabindex="0"]`);
      const customFilter = Array.prototype.filter;
      this.modalElementsFocus(customFilter, tabbableEls, activeModalElement);
      this.focusableEls = Array.prototype.slice.call(this.focusableEls);
      const KEYCODE_TAB = 9;
      this.modalTabEvent(KEYCODE_TAB, activeModalElement);
    }
  }

  modalElementsFocus(customFilter, tabbableEls, activeModalElement) {
    this.focusableEls = customFilter.call(tabbableEls, (el) => {
      let elCopy = el;
      while (elCopy !== activeModalElement) {
        const displayVal = window
          .getComputedStyle(elCopy, null)
          .getPropertyValue('display');
        const visibilityVal = window
          .getComputedStyle(elCopy, null)
          .getPropertyValue('visibility');
        if (displayVal !== 'none' && visibilityVal !== 'hidden') {
          elCopy = elCopy.parentNode;
        } else {
          const hasCloseIcon = elCopy.classList;
          if (
            hasCloseIcon &&
            hasCloseIcon !== undefined &&
            hasCloseIcon !== null
          ) {
            if (
              hasCloseIcon.contains('flyout-close') &&
              hasCloseIcon.contains('icon-moreoptionsclose')
            ) {
              elCopy = elCopy.parentNode;
            }
            return true;
          } else {
            return false;
          }
        }
      }
      return true;
    });
  }

  clearSearchField() {
    this.searchField.nativeElement.value = '';
    this.searchField.nativeElement.focus();
    this.showEmptyMessage = false;
    this.cdref.detectChanges();
  }

  getSiteSearchUrl() {
    this.userDataService.getAuthConfigContent().subscribe((response) => {
      if (response && response['SITE_SEARCH_AWS_ENABLE'] === true) {
        const peRequest: SiteSearchPERequest = {
          userdata: [],
          requestparam: {
            campaignId: 'flowrouter:sitesearch-aws',
            format: 'json'
          }
        };
        this.siteSearchService
          .getSiteSearchUrl(peRequest)
          .subscribe((prResponse: SiteSearchPEResponse) => {
            try {
              if (prResponse && prResponse.contentDetails) {
                this.siteSearchUrl =
                  prResponse.contentDetails.parsys?.router?.endpoint;
              }
            } catch (err) {
              this.loggingService.log(
                'Caught Error FlyoutSearchComponent:: getSiteSearchUrl: ' + err
              );
            }
          });
      }
    });
  }

  ngOnDestroy() {
    if (this.contentHeaderFooterServiceSubscribe) {
      this.contentHeaderFooterServiceSubscribe.unsubscribe();
    }
    if (this.deviceTypeSubscribe) {
      this.deviceTypeSubscribe.unsubscribe();
    }
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.unsubscribe$.unsubscribe();
    this.cdref.detach();
  }
}
